"use client";

import React, { useMemo, useCallback, useState, useRef, useEffect } from "react";
import Image from "next/image";
import clsx from "clsx";
import { Bookmark } from "lucide-react";
import {
  createNameExtractor,
  decodeHtmlEntities,
  formatCatalogPrice,
  formatPrice,
  isPriceOnRequest,
  normalizeLabels,
} from "@org/ui";
import useFavorites from "@/hooks/useFavorites";
import { extractFavoriteData } from "@/lib/favorites";
import { useLanguage } from "@/lib/config/useLanguage";
import { usePathname } from "next/navigation";
import { getBranchCodeFromPath } from "@/lib/config/branches-config";
import { siteConfig } from "@/lib/config/site-config";
import { productItemPath } from "@/lib/catalog/product-path";

function buildImageGallery(item, fallbackImageUrl) {
  const images = [];

  const gallery = item?.media?.gallery;
  if (Array.isArray(gallery) && gallery.length > 0) {
    images.push(...gallery.filter(Boolean));
  }

  const imageGallery = item?.image_gallery;
  if (Array.isArray(imageGallery) && imageGallery.length > 0) {
    images.push(...imageGallery.filter(Boolean));
  }

  const cover = item?.media?.cover;
  if (typeof cover === "string" && cover.trim() && !images.includes(cover)) {
    images.unshift(cover);
  }

  const picture = item?.picture;
  if (typeof picture === "string" && picture.trim() && !images.includes(picture)) {
    images.unshift(picture);
  }

  const image = item?.image;
  if (typeof image === "string" && image.trim() && !images.includes(image)) {
    images.unshift(image);
  }

  const uniqueImages = Array.from(new Set(images.filter(Boolean))).slice(0, 4);
  return uniqueImages.length > 0 ? uniqueImages : [fallbackImageUrl];
}

/**
 * Локальная реализация карточки товара для msk-ur-met-ru.
 * Нужна, чтобы исправить мобильный tap/swipe только в этом приложении
 * и не затрагивать остальных потребителей @org/ui.
 */
export default function ProductCard({
  item,
  showPrices = true,
  showDiscount = true,
  className = "",
  eagerImage = false,
}) {
  const { isFavorite, toggleFavorite, isHydrated } = useFavorites();
  const { langCode, currency, localizePath } = useLanguage();
  const pathname = usePathname() || "/";
  const currentBranchCode = getBranchCodeFromPath(pathname);

  const favoriteData = useMemo(() => extractFavoriteData(item), [item]);
  const labels = useMemo(() => normalizeLabels(item), [item]);
  const getName = useMemo(() => createNameExtractor(), []);
  const productName = useMemo(() => getName(item, langCode) || "Товар", [getName, item, langCode]);
  const favoriteId = favoriteData?.id;
  const favoriteActive = favoriteId && isHydrated ? isFavorite(favoriteId) : false;

  const productCardConfig = siteConfig.ui?.productCard;
  const aspectRatio = productCardConfig?.aspectRatio || "3/4";
  const imageFit = productCardConfig?.imageFit || "contain";
  const fallbackImageUrl = "/placeholder.png";

  const price = useMemo(() => {
    const priceValue = item?.price ?? item?.languages?.[langCode]?.price;
    return typeof priceValue === "number"
      ? priceValue
      : typeof priceValue === "string"
        ? Number(priceValue)
        : 0;
  }, [item, langCode]);

  const priceOld = useMemo(() => {
    const priceOldValue = item?.priceOld ?? item?.languages?.[langCode]?.priceOld;
    return typeof priceOldValue === "number"
      ? priceOldValue
      : typeof priceOldValue === "string"
        ? Number(priceOldValue)
        : 0;
  }, [item, langCode]);

  const measure = useMemo(() => {
    const raw = item?.measure;
    return typeof raw === "string" ? raw.trim() : "";
  }, [item]);

  const imageGallery = useMemo(
    () => buildImageGallery(item, fallbackImageUrl),
    [item],
  );

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Дополнительные фото галереи не попадают в HTML и не грузятся, пока
  // посетитель не навёл/не свайпнул карточку: на мобильном каталоге из 24
  // карточек это ~70 картинок и ~250 КБ разметки srcset (SEO H-15).
  const [galleryActivated, setGalleryActivated] = useState(false);
  const activateGallery = useCallback(() => {
    if (imageGallery.length > 1) setGalleryActivated(true);
  }, [imageGallery.length]);
  const imageContainerRef = useRef(null);
  const isTouchRef = useRef(false);
  const touchStartRef = useRef(null);
  const swipedRef = useRef(false);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [item?.code, item?.id]);

  const itemCode = item?.code ?? item?.id ?? "";
  const itemHref = useMemo(
    () => productItemPath(itemCode, currentBranchCode, localizePath),
    [itemCode, currentBranchCode, localizePath],
  );

  const discountPercent = useMemo(() => {
    if (!showDiscount || isPriceOnRequest(price) || priceOld <= 0 || priceOld <= price) {
      return 0;
    }

    return Math.round(((priceOld - price) / priceOld) * 100);
  }, [price, priceOld, showDiscount]);

  const formattedPrice = formatCatalogPrice(price, currency || undefined, langCode);
  const formattedPriceOld = formatPrice(priceOld, currency || undefined, langCode);
  const showOldPrice =
    !isPriceOnRequest(price) &&
    showDiscount &&
    priceOld > 0 &&
    priceOld > price;

  const handleFavoriteToggle = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    if (favoriteData) {
      toggleFavorite(favoriteData);
    }
  }, [favoriteData, toggleFavorite]);

  const handleMouseMove = useCallback((event) => {
    if (isTouchRef.current) return;
    if (imageGallery.length <= 1 || !imageContainerRef.current) return;
    if (!galleryActivated) setGalleryActivated(true);

    const rect = imageContainerRef.current.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const nextIndex = Math.min(
      Math.floor((mouseX / rect.width) * imageGallery.length),
      imageGallery.length - 1,
    );

    if (nextIndex !== currentImageIndex) {
      setCurrentImageIndex(nextIndex);
    }
  }, [imageGallery.length, currentImageIndex, galleryActivated]);

  const handleMouseLeave = useCallback(() => {
    if (isTouchRef.current) return;
    setCurrentImageIndex(0);
  }, []);

  const handleTouchStart = useCallback((event) => {
    isTouchRef.current = true;

    if (imageGallery.length <= 1) return;
    activateGallery();

    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    swipedRef.current = false;
  }, [imageGallery.length, activateGallery]);

  const handleTouchMove = useCallback((event) => {
    if (!touchStartRef.current || imageGallery.length <= 1) return;

    const touch = event.touches[0];
    const dx = Math.abs(touch.clientX - touchStartRef.current.x);
    const dy = Math.abs(touch.clientY - touchStartRef.current.y);

    if (dx > 15 && dx > dy) {
      swipedRef.current = true;
    }
  }, [imageGallery.length]);

  const handleTouchEnd = useCallback((event) => {
    if (!touchStartRef.current || imageGallery.length <= 1) {
      touchStartRef.current = null;
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    touchStartRef.current = null;

    if (swipedRef.current && Math.abs(deltaX) > 30) {
      event.preventDefault();
      setCurrentImageIndex((prev) => (
        deltaX < 0
          ? Math.min(prev + 1, imageGallery.length - 1)
          : Math.max(prev - 1, 0)
      ));
    }
  }, [imageGallery.length]);

  const handleCardClick = useCallback((event) => {
    if (!swipedRef.current) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    swipedRef.current = false;
  }, []);

  const goToImage = useCallback((index) => {
    if (index >= 0 && index < imageGallery.length) {
      setGalleryActivated(true);
      setCurrentImageIndex(index);
    }
  }, [imageGallery.length]);

  return (
    <div
      className={clsx(
        "relative mb-2 flex w-full flex-col rounded-md bg-white hover:text-blue-500",
        className,
      )}
    >
      {favoriteData && (
        <button
          type="button"
          onClick={handleFavoriteToggle}
          aria-pressed={favoriteActive}
          aria-label={favoriteActive ? `Убрать "${productName}" из сохранённого` : `Сохранить "${productName}"`}
          title={favoriteActive ? 'Убрать из сохранённого' : 'Сохранить'}
          className={clsx(
            "absolute right-2 top-2 z-10 flex h-9 w-9 items-center justify-center rounded-full border bg-white/95 shadow-sm backdrop-blur-sm transition-colors focus:outline-none focus-visible:ring focus-visible:ring-offset-2 focus-visible:ring-gray-400",
            favoriteActive
              ? "border-gray-900 text-gray-900"
              : "border-gray-200 text-gray-600 hover:border-gray-400 hover:text-gray-900",
          )}
        >
          <Bookmark className="h-5 w-5" strokeWidth={2} fill={favoriteActive ? "currentColor" : "none"} aria-hidden="true" />
        </button>
      )}

      <a href={itemHref} className="flex h-full flex-col" onClick={handleCardClick}>
        <div
          ref={imageContainerRef}
          className="relative flex items-center justify-center overflow-hidden rounded border bg-white"
          style={{ minHeight: 0, position: "relative", aspectRatio }}
          onMouseEnter={activateGallery}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {labels.length > 0 && (
            <div className="absolute left-2 top-2 z-10 flex flex-col gap-1">
              {labels.map((label) => {
                const meta = label.meta || {};
                const metaKey =
                  meta.xml_id ||
                  meta.enum_id ||
                  meta.raw ||
                  label.value ||
                  label.name ||
                  label.code ||
                  "label";
                const badgeText = decodeHtmlEntities(label.value || label.name || "");
                const titleSource =
                  meta.name && meta.name !== badgeText ? meta.name : label.name;
                const title =
                  titleSource && titleSource !== badgeText
                    ? decodeHtmlEntities(titleSource)
                    : undefined;

                return (
                  <span
                    key={`${label.code || "label"}:${metaKey}:${label.source || "product"}`}
                    title={title}
                    data-source={label.source || "product"}
                    className={clsx(
                      "inline-flex w-fit items-center rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-wide shadow-sm",
                      meta.bg_color
                        ? ""
                        : label.source === "offer"
                          ? "border-indigo-200 bg-indigo-600/10 text-indigo-600"
                          : "border-gray-900 bg-gray-900 text-white",
                    )}
                    style={{
                      ...(meta.bg_color ? { backgroundColor: meta.bg_color, borderColor: meta.bg_color } : {}),
                      ...(meta.text_color ? { color: meta.text_color } : {}),
                    }}
                  >
                    {badgeText}
                  </span>
                );
              })}
            </div>
          )}

          <div className="relative h-full w-full">
            {imageGallery.map((imageUrl, index) => (index === 0 || galleryActivated) && (
              <div
                key={`${imageUrl}-${index}`}
                className={clsx(
                  "absolute inset-0 transition-opacity duration-300",
                  index === currentImageIndex ? "opacity-100" : "opacity-0",
                )}
              >
                <Image
                  src={imageUrl}
                  alt={`${productName} - ${index + 1}`}
                  fill
                  sizes="(min-width: 1280px) 240px, (min-width: 768px) 220px, 160px"
                  className={clsx("object-center", imageFit === "cover" ? "object-cover" : "object-contain")}
                  quality={75}
                  loading={index === 0 && eagerImage ? "eager" : "lazy"}
                  style={{ backgroundColor: "transparent" }}
                  aria-hidden={index !== currentImageIndex}
                />
              </div>
            ))}
          </div>

          {imageGallery.length > 1 && (
            <div className="absolute inset-x-0 bottom-2 z-10 flex justify-center gap-1.5">
              {imageGallery.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    goToImage(index);
                  }}
                  className={clsx(
                    "h-1.5 w-1.5 rounded-full transition-all duration-200",
                    index === currentImageIndex ? "w-4 bg-blue-500" : "bg-gray-300 hover:bg-gray-400",
                  )}
                  aria-label={`Перейти к изображению ${index + 1}`}
                  aria-current={index === currentImageIndex ? "true" : "false"}
                />
              ))}
            </div>
          )}
        </div>

        <div className="p-2">
          {showPrices && showDiscount && discountPercent > 0 && (
            <div className="mb-1 text-xs font-medium text-red-500">-{discountPercent}%</div>
          )}

          {showPrices && (
            <div className="flex items-center gap-2">
              {formattedPrice && (
                <div className="font-bold text-default-900">
                  {formattedPrice}
                  {measure && !isPriceOnRequest(price) ? (
                    <span className="ml-1.5 text-base font-normal text-gray-500">/ {measure}</span>
                  ) : null}
                </div>
              )}
              {showOldPrice && formattedPriceOld && (
                <div className="text-sm text-gray-400 line-through">{formattedPriceOld}</div>
              )}
            </div>
          )}

          <div
            className="min-h-[3.5rem] text-sm leading-snug line-clamp-3"
            title={decodeHtmlEntities(productName)}
          >
            {decodeHtmlEntities(productName)}
          </div>
        </div>
      </a>
    </div>
  );
}
